import React, { useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';

// ----------------------------------------------------------------------
// PageHero — the cinematic intro used at the top of every inner page.
// Shorter than the homepage CinematicHero (no 1.6vh pin), but uses the
// same brand language: dark navy curtain over a photograph, a mono
// kicker rail, a Kufam display headline, optional italic accent, an
// optional supporting line, and an optional CTA pair.
// ----------------------------------------------------------------------
export default function PageHero({
  kicker = 'Kehilat Bnei Torah  ·  Los Angeles',
  meta = '9017 W. Pico Blvd.',
  title,
  titleAccent,
  subtitle,
  image = '/assets/images/kbt/night-seder.jpg',
  height = '78vh',
  minHeight = 520,
  align = 'left',
  primaryCta,
  secondaryCta,
  children,
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const photoScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.14]);
  const photoY = useTransform(scrollYProgress, [0, 1], ['0%', '-8%']);
  const titleY = useTransform(scrollYProgress, [0, 1], ['0%', '-12%']);
  const titleOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  const center = align === 'center';

  return (
    <Box
      ref={ref}
      component="section"
      sx={{
        position: 'relative',
        width: '100%',
        // `height` is a FLOOR, not a fixed size. It used to be a hard
        // height alongside overflow:hidden, so any headline longer than
        // the viewport allowed got clipped mid-glyph instead of the hero
        // growing to fit. Pages whose copy already fits render
        // identically; long ones now grow instead of losing text.
        minHeight: `max(${height}, ${typeof minHeight === 'number' ? `${minHeight}px` : minHeight})`,
        background: 'var(--navy-900)',
        overflow: 'hidden',
        color: '#fff',
      }}
    >
      {/* photo */}
      <motion.div
        aria-hidden
        style={{
          position: 'absolute',
          inset: '-6% -2%',
          backgroundImage: `url('${image}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
          scale: photoScale,
          y: photoY,
          zIndex: 0,
        }}
      />
      {/* navy curtain */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(7,6,26,0.62) 0%, rgba(7,6,26,0.55) 40%, rgba(7,6,26,0.92) 100%)',
          zIndex: 1,
        }}
      />
      {/* spotlight */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 80% 65% at 50% 70%, rgba(36,34,88,0.55), transparent 70%)',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      {/* content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 3,
          height: '100%',
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
        }}
      >
        {/* top rail */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: { xs: 3, md: 6 },
            pt: { xs: 14, md: 16 },
            fontFamily: 'var(--font-mono)',
            fontSize: 10.5,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--teal-100)',
            gap: 3,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            {kicker}
          </motion.div>
          {meta ? (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'var(--teal-300)',
                  display: 'inline-block',
                }}
              />
              {meta}
            </motion.div>
          ) : <span />}
        </Box>

        {/* center stage */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: center ? 'center' : 'flex-start',
            px: { xs: 3, md: 6 },
            py: { xs: 6, md: 4 },
          }}
        >
          <motion.div style={{ y: titleY, opacity: titleOpacity, width: '100%' }}>
            <Stack
              alignItems={center ? 'center' : 'flex-start'}
              spacing={3}
              sx={{
                maxWidth: 1100,
                mx: center ? 'auto' : 0,
                textAlign: center ? 'center' : 'left',
              }}
            >
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  lineHeight: 0.96,
                  letterSpacing: '-0.02em',
                  color: '#fff',
                  fontSize: 'clamp(40px, 7vw, 104px)',
                  textShadow: '0 2px 40px rgba(7,6,26,0.4)',
                }}
              >
                {title}
                {titleAccent && (
                  <>
                    <br />
                    <span
                      style={{
                        fontStyle: 'italic',
                        fontWeight: 500,
                        color: 'var(--teal-300)',
                      }}
                    >
                      {titleAccent}
                    </span>
                  </>
                )}
              </motion.h1>

              {subtitle && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontWeight: 400,
                    fontSize: 'clamp(15px, 1.5vw, 19px)',
                    color: 'rgba(255,255,255,0.86)',
                    maxWidth: 640,
                    lineHeight: 1.55,
                  }}
                >
                  {subtitle}
                </motion.div>
              )}

              {(primaryCta || secondaryCta) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    display: 'flex',
                    gap: 14,
                    marginTop: 4,
                    flexWrap: 'wrap',
                    justifyContent: center ? 'center' : 'flex-start',
                  }}
                >
                  {primaryCta && (
                    <Box
                      onClick={primaryCta.onClick}
                      component={primaryCta.href ? 'a' : 'div'}
                      href={primaryCta.href}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: 'none',
                        px: 3.5,
                        py: 1.6,
                        background: 'var(--teal-500)',
                        color: '#fff',
                        fontFamily: 'var(--font-body)',
                        fontWeight: 700,
                        fontSize: 12.5,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        borderRadius: 999,
                        transition: 'background 250ms ease, transform 250ms ease',
                        '&:hover': { background: 'var(--teal-700)', transform: 'translateY(-2px)' },
                      }}
                    >
                      {primaryCta.label}
                    </Box>
                  )}
                  {secondaryCta && (
                    <Box
                      onClick={secondaryCta.onClick}
                      component={secondaryCta.href ? 'a' : 'div'}
                      href={secondaryCta.href}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: 'none',
                        px: 3.5,
                        py: 1.6,
                        background: 'transparent',
                        color: '#fff',
                        border: '1.5px solid rgba(255,255,255,0.6)',
                        fontFamily: 'var(--font-body)',
                        fontWeight: 700,
                        fontSize: 12.5,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        borderRadius: 999,
                        transition: 'all 250ms ease',
                        '&:hover': {
                          background: '#fff',
                          color: 'var(--navy-500)',
                          borderColor: '#fff',
                        },
                      }}
                    >
                      {secondaryCta.label}
                    </Box>
                  )}
                </motion.div>
              )}

              {children}
            </Stack>
          </motion.div>
        </Box>

        {/* bottom strap */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            px: { xs: 3, md: 6 },
            pb: { xs: 3, md: 4 },
            color: 'var(--teal-100)',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            opacity: 0.85,
          }}
        >
          <Box>Scroll  ↓</Box>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            More than just a Beit Knesset
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
